// src/MineralIdentification.js
import React, { useState, useEffect } from 'react';
import { db, storage, functions } from './firebase.js';
import { httpsCallable } from 'firebase/functions';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const MineralIdentification = ({ user, isAdmin, isMobile }) => {
  const [minerals, setMinerals] = useState([]);
  const [editingMineral, setEditingMineral] = useState(null);
  const [flippedCard, setFlippedCard] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [status, setStatus] = useState(null);
  const [reclassifying, setReclassifying] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');

  // Singular for filtering (matches API), but we'll map to plural for display
  const mineralTypes = ['mineral', 'rock', 'gemstone', 'other'];
  const typeDisplayMap = {
    'mineral': 'Minerals',
    'rock': 'Rocks',
    'gemstone': 'Gemstones',
    'other': 'Other'
  };

  useEffect(() => {
    console.log('Setting up Firestore listener for minerals collection');
    const unsubscribe = onSnapshot(collection(db, 'minerals'), (snapshot) => {
      const fetchedMinerals = snapshot.docs.map(doc => {
        const data = doc.data();
        const displayImageUrl = data.imageUrl || (data.images && data.images[0]) || 'undefined';
        console.log(`Mineral ID: ${doc.id}, imageUrl: ${displayImageUrl}`);
        return { 
          id: doc.id, 
          ...data, 
          displayImageUrl,
          type: data.type ? data.type.toLowerCase().replace(/s$/, '') : 'other'  // Normalize: lowercase, remove plural 's'
        };
      });
      console.log('Fetched minerals:', fetchedMinerals);
      if (snapshot.empty) {
        console.log('No documents found in minerals collection');
      }
      setMinerals(fetchedMinerals);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching minerals:', error);
      setError(error.message);
      setLoading(false);
    });
    return () => {
      console.log('Cleaning up Firestore listener');
      unsubscribe();
    };
  }, []);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    console.log('Selected file:', file?.name || 'none');
    if (!file) return;
    setUploadError(null);
    setStatus('Uploading...');
    try {
      const storageRef = ref(storage, `mineral_images/${Date.now()}_${file.name}`);
      console.log('Uploading file to:', storageRef.fullPath);
      const uploadResult = await uploadBytes(storageRef, file);
      console.log('Upload result:', uploadResult);
      const imageUrl = await getDownloadURL(storageRef);
      console.log('Generated imageUrl:', imageUrl);

      setStatus('Processing...');
      const identifyMineral = httpsCallable(functions, 'apiIdentifyMineral');
      const result = await identifyMineral({ imageUrl });
      console.log('Mineral identification result:', result.data);

      let mineralData = result.data;
      if (mineralData.error || !mineralData) {
        console.log('Mineral unclassified or no data, creating default data');
        mineralData = {
          type: 'other',
          common_name: 'Unclassified',
          scientific_name: 'Unknown',
          composition: 'Unknown',
          hardness: 'Unknown',
          uses: 'Unknown',
          location_found: 'Unknown',
          imageUrl,
          images: [imageUrl],
          created_by: user.uid
        };
      } else {
        mineralData.location_found = mineralData.location_found ? 'Yes' : 'No';
        mineralData.imageUrl = imageUrl;
        mineralData.images = [imageUrl];
        mineralData.created_by = user.uid;
        mineralData.type = mineralData.type ? mineralData.type.toLowerCase().replace(/s$/, '') : 'other';  // Normalize
      }

      setStatus('Creating card...');
      const docRef = await addDoc(collection(db, 'minerals'), mineralData);
      console.log('Added mineral document with ID:', docRef.id, 'imageUrl:', imageUrl);
      setUploadError(null);
      setStatus(null);
    } catch (error) {
      console.error('Upload/Identification error:', error);
      setUploadError(error.message || 'Internal error');
      const storageRef = ref(storage, `mineral_images/${Date.now()}_${file.name}`);
      console.log('Uploading fallback file to:', storageRef.fullPath);
      const uploadResult = await uploadBytes(storageRef, file);
      console.log('Fallback upload result:', uploadResult);
      const imageUrl = await getDownloadURL(storageRef);
      console.log('Generated fallback imageUrl:', imageUrl);
      setStatus('Creating fallback card...');
      const docRef = await addDoc(collection(db, 'minerals'), {
        type: 'other',
        common_name: 'Unclassified',
        scientific_name: 'Unknown',
        composition: 'Unknown',
        hardness: 'Unknown',
        uses: 'Unknown',
        location_found: 'Unknown',
        imageUrl,
        images: [imageUrl],
        created_by: user.uid
      });
      console.log('Added fallback mineral document with ID:', docRef.id, 'imageUrl:', imageUrl);
      setStatus(null);
    }
  };

  // Batch reclassify uncategorized minerals
  const reclassifyUncategorized = async () => {
    setReclassifying(true);
    setStatus('Reclassifying minerals... This may take a minute.');
    try {
      const uncategorized = minerals.filter(p => p.type === 'other');
      console.log(`Reclassifying ${uncategorized.length} minerals...`);

      for (const mineral of uncategorized) {
        if (!mineral.imageUrl) {
          console.warn(`Skipping mineral ${mineral.id}: No imageUrl`);
          continue;
        }

        const identifyMineral = httpsCallable(functions, 'apiIdentifyMineral');
        const result = await identifyMineral({ imageUrl: mineral.imageUrl });
        console.log(`Re-identification for ${mineral.id}:`, result.data);

        let updatedData = result.data;
        if (!updatedData.error && updatedData) {
          updatedData.type = updatedData.type ? updatedData.type.toLowerCase().replace(/s$/, '') : 'other';
          updatedData.location_found = updatedData.location_found ? 'Yes' : 'No';
          // Optionally update other fields if desired (e.g., habitat, uses)
        } else {
          console.warn(`Failed to reclassify ${mineral.id}, keeping as 'other'`);
          continue;
        }

        await updateDoc(doc(db, 'minerals', mineral.id), updatedData);
        console.log(`Updated mineral ${mineral.id} with type: ${updatedData.type}`);
      }

      setStatus('Reclassification complete! Refresh if needed.');
    } catch (err) {
      console.error('Reclassification error:', err);
      setStatus(`Error during reclassification: ${err.message}`);
    } finally {
      setReclassifying(false);
    }
  };

  const handleEdit = (mineral) => {
    console.log('Editing mineral:', mineral.id);
    setEditingMineral(mineral);
  };

  const handleSaveEdit = async () => {
    console.log('Saving edited mineral:', editingMineral.id);
    await updateDoc(doc(db, 'minerals', editingMineral.id), editingMineral);
    setEditingMineral(null);
  };

  const handleDelete = async (id) => {
    console.log('Deleting mineral:', id);
    await deleteDoc(doc(db, 'minerals', id));
  };

  const toggleFlip = (id) => {
    console.log('Toggling flip for mineral:', id);
    setFlippedCard(prev => prev === id ? null : id);
  };

  const handleImageError = (id, url) => {
    console.error(`Image failed to load for mineral ID: ${id}, URL: ${url || 'undefined'}`);
    setImageErrors(prev => ({ ...prev, [id]: true }));
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  if (loading) {
    return <div className="text-center text-xl mt-6">Loading...</div>;
  }

  if (error) {
    return <div className="text-center text-red-500 mt-6">Error: {error}</div>;
  }

  return (
    <div className={`flora-page ${isMobile ? 'flora-page-mobile' : ''}`}>
      <h2 className="text-2xl font-bold text-center mb-6">Rock and Mineral Identification</h2>
      {user && (
        <>
          <p className="text-center text-gray-600 mb-4">Upload an image to have it identified. Clicking on a card will reveal more information on the mineral.</p>
          {uploadError && (
            <div className="text-center text-red-500 mb-4">Upload error: {uploadError}</div>
          )}
          {status && (
            <div className="text-center text-blue-500 mb-4">{status}</div>
          )}
          <label
            className="header-nav-button block w-full max-w-[10rem] ml-0 mb-6 text-center cursor-pointer"
          >
            Upload image
            <input
              type="file"
              hidden
              onChange={handleFileChange}
              accept="image/*"
            />
          </label>
          {isAdmin && (
            <button
              onClick={reclassifyUncategorized}
              disabled={reclassifying}
              className="header-nav-button block w-full max-w-[10rem] ml-0 mb-6 text-center"
            >
              {reclassifying ? 'Reclassifying...' : 'Reclassify'}
            </button>
          )}
        </>
      )}
      <select
        value={selectedCategory}
        onChange={handleCategoryChange}
        className="block w-full max-w-[15rem] ml-0 mb-6 p-2 border border-gray-300 rounded"
      >
        <option value="All">All Categories</option>
        {mineralTypes.map(type => (
          <option key={type} value={type}>
            {typeDisplayMap[type]}
          </option>
        ))}
      </select>

      {minerals.length === 0 && (
        <p className="text-center text-gray-500">No minerals found. Upload a new image to start.</p>
      )}

      {(selectedCategory === 'All' ? mineralTypes : [selectedCategory]).map(type => (
        <div key={type}>
          <h3 className="text-xl font-semibold mt-6 mb-4">{typeDisplayMap[type]}</h3>
          <div className="flora-grid">
            {minerals.filter(mineral => mineral.type === type).map(mineral => (
              <div key={mineral.id} className="card-container">
                <div className={`card ${flippedCard === mineral.id ? 'flipped' : ''}`} onClick={() => toggleFlip(mineral.id)}>
                  <div className="card-front">
                    <div className="card-image-area">
                      {imageErrors[mineral.id] || !mineral.displayImageUrl ? (
                        <img
                          src="https://placehold.co/400x320/e5e7eb/6b7280?text=No+Image&font=roboto"
                          alt="Unavailable"
                        />
                      ) : (
                        <img
                          src={mineral.displayImageUrl}
                          alt={mineral.common_name}
                          onError={() => handleImageError(mineral.id, mineral.displayImageUrl)}
                        />
                      )}
                    </div>
                    <div className="card-info">
                      <h3>{mineral.common_name}</h3>
                      <p>Scientific: {mineral.scientific_name}</p>
                    </div>
                    {user && (
                      <div className="card-actions">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleEdit(mineral); }}
                          className="admin-users-action-button"
                        >
                          Edit
                        </button>
                        {isAdmin && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleDelete(mineral.id); }}
                            className="admin-users-delete-button"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="card-back bg-gray-300 text-left">
                    <p className="text-sm"><strong>Composition:</strong> {mineral.composition}</p>
                    <p className="text-sm"><strong>Hardness:</strong> {mineral.hardness}</p>
                    <p className="text-sm"><strong>Uses:</strong> {mineral.uses}</p>
                    <p className="text-sm"><strong>Location Found:</strong> {mineral.location_found}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {editingMineral && (
        <div className={`modal-overlay ${isMobile ? 'modal-content-mobile' : ''}`}>
          <div className="modal-content">
            <h2 className="modal-title">Edit Mineral</h2>
            <input
              type="text"
              value={editingMineral.type}
              onChange={e => setEditingMineral({ ...editingMineral, type: e.target.value })}
              placeholder="Type (e.g., rock)"
              className="modal-input h-16"
            />
            <input
              type="text"
              value={editingMineral.common_name}
              onChange={e => setEditingMineral({ ...editingMineral, common_name: e.target.value })}
              placeholder="Common Name"
              className="modal-input h-16"
            />
            <input
              type="text"
              value={editingMineral.scientific_name}
              onChange={e => setEditingMineral({ ...editingMineral, scientific_name: e.target.value })}
              placeholder="Scientific Name"
              className="modal-input h-16"
            />
            <textarea
              value={editingMineral.composition}
              onChange={e => setEditingMineral({ ...editingMineral, composition: e.target.value })}
              placeholder="Composition"
              className="modal-textarea h-24"
            />
            <input
              type="text"
              value={editingMineral.hardness}
              onChange={e => setEditingMineral({ ...editingMineral, hardness: e.target.value })}
              placeholder="Hardness"
              className="modal-input h-16"
            />
            <textarea
              value={editingMineral.uses}
              onChange={e => setEditingMineral({ ...editingMineral, uses: e.target.value })}
              placeholder="Uses"
              className="modal-textarea h-24"
            />
            <input
              type="text"
              value={editingMineral.location_found}
              onChange={e => setEditingMineral({ ...editingMineral, location_found: e.target.value })}
              placeholder="Location Found (Yes/No)"
              className="modal-input h-16"
            />
            <div className="modal-button-group">
              <button
                onClick={handleSaveEdit}
                className="modal-button modal-button-primary"
              >
                Save
              </button>
              <button
                onClick={() => setEditingMineral(null)}
                className="modal-button modal-button-secondary"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MineralIdentification;